import {
  Component,
  OnInit,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { subDays, startOfToday } from 'date-fns';
import { of, Subject } from 'rxjs';
import { debounceTime, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { DateRangePeriod } from 'src/app/core/models/user';
import {
  AdHistoryLog,
  AdHistoryLogDetail,
  AdDetailLogCampaign,
} from 'src/app/core/models/ad-history-log';
import { AdHistoryLogService } from 'src/app/core/services/ad-history-log.service';
import { downloadFile } from 'src/app/core/utils';
import { GeneralWarningModalComponent } from '../../forecastui/general-warning-modal/general-warning-modal.component';
import { ProductAdsActionService } from 'src/app/routes/dashboard/amz-advertising/services/product-ads-action.service';
import { TreelistColumnDef } from '../../forecastui/forecast-rxtreelist/treelist-column-def.interface';
import { TreelistLevelConfig } from '../../forecastui/forecast-rxtreelist/treelist-level-config.interface';

import {
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
} from '@angular/animations';

@Component({
  selector: 'app-product-ads-history-log',
  templateUrl: './product-ads-history-log.component.html',
  styleUrls: ['./product-ads-history-log.component.scss'],
  animations: [
    trigger('listAnimation', [
      transition('* <=> *', [
        query(
          ':enter',
          [
            style({
              opacity: 0,
              transform: 'translateY(-20px)',
              maxHeight: 0,
              margin: 0,
              padding: 0,
            }),
            stagger(100, [
              animate(
                '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                style({
                  opacity: 1,
                  transform: 'translateY(0)',
                  maxHeight: '400px',
                  margin: '*',
                  padding: '*',
                }),
              ),
            ]),
          ],
          { optional: true },
        ),
      ]),
    ]),
    trigger('itemAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-30px)', maxHeight: 0 }),
        animate(
          '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateX(0)', maxHeight: '400px' }),
        ),
      ]),
    ]),
  ],
})
export class ProductAdsHistoryLogComponent implements OnInit, OnDestroy {
  public isOpened = false;
  public isLoading = false;
  public isReverting = false;

  public logs: AdHistoryLog[] = [];
  public total = 0;
  public currentPage = 0;
  public pageSize = 10;

  public isDetailOpen = false;
  public isCombineLog = false;
  public selectedLog: AdHistoryLog | null = null;
  public selectedLogDetail: AdHistoryLogDetail | null = null;
  public combinedStats: {
    totalCount: number;
    appliedCount: number;
    failedCount: number;
  } | null = null;
  public isDetailLoading = false;

  // Detail paging / sort / search state
  public detailTotal = 0;
  public detailPageSize = 20;
  public detailSkip = 0;
  public detailSort: Array<{ field: string; dir: string }> = [
    { field: 'sku', dir: 'asc' },
  ];
  public detailSearch = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  @ViewChild('drawerBody') drawerBody: ElementRef;

  // ─── Treelist config for detail modal ────────────────────────────────────

  public detailRootItems: any[] = [];

  // this variable will handle calling from outside, for example: from alert-product-ads-history-logs-logs-component
  // when click on "View Log" button
  public filterOverride: {
    triggerType?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
    ids?: string[];
  } | null = null;

  // this variable makes sure the tag "ALL" will be hide
  // when user click the item all in multiple select kendo
  public tagMapper = (tags: any[]): any[] => {
    return tags.filter((t) => t.value !== 'ALL');
  };

  // this variable makes sure when user toggle "All" to off,
  // the dropdown will keep the clear dropdown when selected All
  private prevSelectedActionTypes: any[] = [];

  // Flag to prevent the document:click handler from closing the drawer
  // immediately after it was opened externally (e.g. from the alert button)
  private skipNextDocumentClick = false;

  get activeDetailColumns() {
    if (this.isCombineLog) {
      return [
        ...this.detailColumns,
        {
          field: 'updatedAt',
          title: 'Updated At',
          width: 160,
          cellType: 'text',
          sortable: true,
          visible: true,
          hiddenInColumnChooser: true,
        },
        {
          field: 'logId',
          title: 'Log ID',
          width: 160,
          cellType: 'text',
          sortable: false,
          visible: true,
          hiddenInColumnChooser: true,
        },
      ];
    }
    return this.detailColumns;
  }

  public readonly detailColumns: TreelistColumnDef[] = [
    // expandable: true — Kendo places the expand arrow here; child rows show campaign name in this column
    {
      field: 'sku',
      title: 'SKU / Campaign',
      width: 220,
      cellType: 'text',
      sortable: true,
      filterable: false,
      filterType: 'text',
      visible: true,
      expandable: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'asin',
      title: 'ASIN',
      width: 120,
      cellType: 'text',
      sortable: true,
      filterable: false,
      filterType: 'text',
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'prevState',
      title: 'Prev State',
      width: 150,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'newState',
      title: 'New State',
      width: 150,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'inventoryStatus',
      title: 'Inventory Status',
      width: 200,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'inventoryAvailable',
      title: 'Inventory Available',
      width: 170,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'reason',
      title: 'Reason',
      width: 500,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'saleVelocity',
      title: 'Sale Velocity',
      width: 110,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'daysUntilStockout',
      title: 'Days Until Stockout',
      width: 150,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'min',
      title: 'Min Days (Config)',
      width: 130,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
    {
      field: 'max',
      title: 'Max Days (Config)',
      width: 130,
      cellType: 'text',
      sortable: false,
      filterable: false,
      visible: true,
      lockable: false,
      hiddenInColumnChooser: true,
    },
  ];

  public readonly treeLevelConfigs: TreelistLevelConfig[] = [
    {
      name: 'Campaigns',
      hasChildren: (dataItem: any) =>
        !!(dataItem._campaigns && dataItem._campaigns.length > 0),
      fetch: (parentId: any) => {
        const parent = this.detailRootItems.find(
          (r: any) => String(r._id) === String(parentId),
        );
        if (!parent) {
          return of([]);
        }
        const children: any[] = [];
        const campaigns: AdDetailLogCampaign[] = parent._campaigns || [];
        for (let i = 0; i < campaigns.length; i++) {
          const campaign = campaigns[i];
          const adGroups = campaign.adGroups || [];
          for (let j = 0; j < adGroups.length; j++) {
            const ag = adGroups[j];
            children.push({
              _id:
                (campaign.campaignId || String(i)) +
                '_' +
                (ag.adId || String(j)),
              // sku holds the campaign name so it appears indented in the expandable column (matches image 1 style)
              sku: campaign.campaignName || '',
              asin: '',
              campaignState: campaign.campaignState || '',
              adGroup: ag.adGroup || '',
              adId: ag.adId || '',
              prevState: ag.prevState || '',
              newState: ag.changedState || '',
              inventoryStatus: '',
              suggestAction: '',
              inventoryAvailable: '',
              saleVelocity: '',
              daysUntilStockout: '',
              min: '',
              max: '',
              reason: '',
              errors: ag.error || '',
              _campaigns: [],
            });
          }
        }
        return of(children);
      },
    },
  ];

  public actionTypes = [
    { text: 'All actions', value: 'ALL' },
    { text: 'Auto Pause', value: 'AUTO_PAUSE' },
    { text: 'Auto Enable', value: 'AUTO_ENABLE' },
    { text: 'Manual Pause', value: 'MANUAL_PAUSE' },
    { text: 'Manual Enable', value: 'MANUAL_ENABLE' },
    { text: 'Revert Enable', value: 'REVERT_ENABLE' },
    { text: 'Revert Pause', value: 'REVERT_PAUSE' },
  ];

  public selectedActionTypes = [];
  public range: {
    type: DateRangePeriod;
    start: Date | null;
    end: Date | null;
  } = {
    type: DateRangePeriod.LAST_90_DAYS,
    start: subDays(startOfToday(), 90),
    end: startOfToday(),
  };

  constructor(
    private elRef: ElementRef,
    private adHistoryLogService: AdHistoryLogService,
    private modalService: NgbModal,
    private productAdsActionService: ProductAdsActionService,
  ) {}

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.isDetailOpen) {
      this.closeDetail();
      return;
    }
    if (this.isOpened) {
      this.closeDrawer();
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.skipNextDocumentClick) {
      this.skipNextDocumentClick = false;
      return;
    }
    if (this.isOpened && !this.isDetailOpen) {
      const target = event.target as HTMLElement;
      if (!document.contains(target)) {
        return;
      }
      const clickedInside = this.elRef.nativeElement.contains(target);
      if (clickedInside) {
        return;
      }
      const isPopup = !!target.closest(
        '.k-animation-container, .k-popup, .k-overlay, .k-list-container, .modal, .modal-backdrop',
      );
      if (isPopup) {
        return;
      }
      this.closeDrawer();
    }
  }

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.detailSearch = term;
        this.detailSkip = 0;
        if (this.selectedLog) {
          this.loadDetail(this.selectedLog._id);
        } else if (this.isCombineLog) {
          this.loadCombinedDetail();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // mapping action type to trigger type and action
  private static readonly ACTION_TYPE_MAP: Record<
    string,
    { triggerType: string; action: string }
  > = {
    AUTO_PAUSE: { triggerType: 'AUTO', action: 'PAUSE' },
    AUTO_ENABLE: { triggerType: 'AUTO', action: 'UNPAUSE' },
    MANUAL_PAUSE: { triggerType: 'MANUAL', action: 'PAUSE' },
    MANUAL_ENABLE: { triggerType: 'MANUAL', action: 'UNPAUSE' },
    REVERT_ENABLE: { triggerType: 'REVERT', action: 'UNPAUSE' },
    REVERT_PAUSE: { triggerType: 'REVERT', action: 'PAUSE' },
  };

  /**
   * Maps a dropdown action type value to its API trigger/action filters.
   */
  private mapActionType(
    value: string,
  ): { triggerType: string; action: string } | null {
    return ProductAdsHistoryLogComponent.ACTION_TYPE_MAP[value] || null;
  }

  private buildQueryParams(actionValue: string): {
    triggerType?: string;
    action?: string;
  } {
    const mappedAction = this.mapActionType(actionValue);
    return mappedAction !== null ? mappedAction : {};
  }

  private handleToggleAll(): void {
    const hasAll = this.selectedActionTypes.some(
      (item) => item.value === 'ALL',
    );
    const wasAllSelected = this.prevSelectedActionTypes.some(
      (item) => item.value === 'ALL',
    );
    // 1. Handle Toggle Logic
    if (hasAll && !wasAllSelected) {
      // Toggle ON: Select everything (KEEP 'ALL' in the array so it stays checked!)
      this.selectedActionTypes = [...this.actionTypes];
      this.prevSelectedActionTypes = [...this.selectedActionTypes];
      return;
    }
    if (!hasAll && wasAllSelected) {
      // Toggle OFF: Clear everything
      this.selectedActionTypes = [];
      this.prevSelectedActionTypes = [];
      return;
    }
    if (hasAll && this.selectedActionTypes.length < this.actionTypes.length) {
      // If a single item is unchecked while ALL was on, remove the ALL check
      this.selectedActionTypes = this.selectedActionTypes.filter(
        (t) => t.value !== 'ALL',
      );
    }
    // Sync the tracker for the next interaction
    this.prevSelectedActionTypes = [...this.selectedActionTypes];
  }

  public loadLogs(silent = false): void {
    if (!silent) {
      this.isLoading = true;
    }

    const params: any = {
      offset: this.currentPage * this.pageSize,
      limit: this.pageSize,
    };

    if (this.range && this.range.start) {
      const start = new Date(this.range.start);
      start.setHours(0, 0, 0, 0);
      params.startDate = start.toISOString();
    }

    if (this.range && this.range.end) {
      const end = new Date(this.range.end);
      end.setHours(23, 59, 59, 999);
      params.endDate = end.toISOString();
    }

    // this will handle toggle all logic
    this.handleToggleAll();
    // recheck has all after toggle all
    const recheckHasAll = this.selectedActionTypes.some(
      (item) => item.value === 'ALL',
    );
    // build action filters
    const actionFilters = recheckHasAll
      ? []
      : this.selectedActionTypes
          .map((t) => this.buildQueryParams(t.value))
          .filter((f) => f !== null);
    params.actionFilters = JSON.stringify(actionFilters);

    this.adHistoryLogService.getList(params).subscribe(
      (res) => {
        this.logs = res.data;
        this.total = res.total;
        this.isLoading = false;
        this.isReverting = false;
      },
      () => {
        this.isLoading = false;
        this.isReverting = false;
      },
    );
  }

  public loadDetail(logId: string): void {
    this.isDetailLoading = true;
    const sortEntry =
      this.detailSort && this.detailSort.length > 0 ? this.detailSort[0] : null;
    const params: any = {
      limit: this.detailPageSize,
      offset: this.detailSkip,
    };
    if (this.detailSearch) {
      params.search = this.detailSearch;
    }
    if (sortEntry) {
      params.sortField = sortEntry.field;
      params.sortDir = sortEntry.dir;
    }
    this.adHistoryLogService.getDetail(logId, params).subscribe(
      (res) => {
        this.selectedLogDetail = res;
        this.detailTotal = res.details.total;
        this.detailRootItems = this.buildDetailRootItems(res);
        this.isDetailLoading = false;
      },
      () => {
        this.isDetailLoading = false;
      },
    );
  }

  public onDetailPageChange(event: { skip: number; take: number }): void {
    this.detailSkip = event.skip;
    this.detailPageSize = event.take;
    if (this.selectedLog) {
      this.loadDetail(this.selectedLog._id);
    } else if (this.isCombineLog) {
      this.loadCombinedDetail();
    }
  }

  public onDetailSortFilterChange(event: {
    sort: Array<{ field: string; dir: string }>;
    filter: any;
  }): void {
    this.detailSort = event.sort;
    this.detailSkip = 0;
    if (this.selectedLog) {
      this.loadDetail(this.selectedLog._id);
    } else if (this.isCombineLog) {
      this.loadCombinedDetail();
    }
  }

  public onDetailSortChange(sort: Array<{ field: string; dir: string }>): void {
    this.detailSort = sort;
    this.detailSkip = 0;
    if (this.selectedLog) {
      this.loadDetail(this.selectedLog._id);
    } else if (this.isCombineLog) {
      this.loadCombinedDetail();
    }
  }

  public onDetailSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  public clearSearch(): void {
    this.detailSearch = '';
    this.searchSubject.next('');
  }

  public onDateRangeChange(range: any): void {
    this.range = range;
    this.currentPage = 0;
    this.loadLogs();
  }

  public onPrevPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadLogs();
    }
  }

  public onNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadLogs();
    }
  }

  public getActionLabel(log: AdHistoryLog): string {
    if (log.triggerType === 'REVERT') {
      const action = log.action === 'PAUSE' ? 'Pause' : 'Enable';
      return 'Reverted ' + action;
    }
    const trigger = log.triggerType === 'AUTO' ? 'Auto' : 'Manual';
    const action = log.action === 'PAUSE' ? 'Paused' : 'Enabled';
    return trigger + ' ' + action;
  }

  public onLogClick(log: AdHistoryLog): void {
    this.selectedLog = log;
    this.isCombineLog = false;
    this.selectedLogDetail = null;
    this.isDetailOpen = true;
    this.detailSkip = 0;
    this.detailSearch = '';
    this.detailSort = [{ field: 'sku', dir: 'asc' }];
    this.loadDetail(log._id);
  }

  public closeDetail(): void {
    this.isDetailOpen = false;
    this.selectedLog = null;
    this.isCombineLog = false;
    this.selectedLogDetail = null;
    this.detailRootItems = [];
  }

  public onDownloadSnapshot(log: AdHistoryLog): void {
    this.adHistoryLogService
      .downloadExcel(log._id)
      .subscribe(({ blob, filename }) => {
        downloadFile(blob as any, filename);
      });
  }

  public onDownloadAllSnapshots(): void {
    const ids = this.filterOverride.ids || [];
    this.adHistoryLogService
      .downloadExcels(ids)
      .subscribe(({ blob, filename }) => {
        downloadFile(blob as any, filename);
      });
  }

  public onRevert(log: AdHistoryLog, event: Event): void {
    event.stopPropagation();
    const action = log.action === 'PAUSE' ? 'paused' : 'enabled';
    const count = log.totalCount || 0;

    const modalRef = this.modalService.open(GeneralWarningModalComponent, {
      centered: true,
      backdrop: 'static',
    });
    modalRef.componentInstance.title = 'Confirm Revert';
    modalRef.componentInstance.content = `You are about to <strong>revert this action</strong> — ${count} SKU(s) that were ${action} will be restored to their previous state.`;
    modalRef.componentInstance.confirmText = 'Revert';

    modalRef.result
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.scrollToTop();
        this.isReverting = true;
        this.isDetailLoading = true;
        this.adHistoryLogService.revert(log._id).subscribe(
          () => {
            this.loadLogs(true);
            if (this.isDetailOpen && this.selectedLog) {
              this.loadDetail(this.selectedLog._id);
            }
            this.productAdsActionService.notifyExternalMutation();
          },
          (err) => {
            console.error('Revert failed', err);
            this.isLoading = false;
            this.isReverting = false;
            this.isDetailLoading = false;
          },
        );
      })
      .catch(() => {
        /* dismissed */
      });
  }

  public onReverts(logs: AdHistoryLog[], event: Event): void {
    event.stopPropagation();

    // Use the exact count and action from the combined stats and filter override
    const action =
      this.filterOverride?.action === 'PAUSE' ? 'paused' : 'enabled';
    const count =
      this.combinedStats?.totalCount ||
      logs.reduce((acc, log) => acc + (log.totalCount || 0), 0);
    // Get the exact full list of IDs from the filter override (which came from /count-automatic)
    const idsToRevert = this.filterOverride?.ids || logs.map((log) => log._id);

    const modalRef = this.modalService.open(GeneralWarningModalComponent, {
      centered: true,
      backdrop: 'static',
    });
    modalRef.componentInstance.title = 'Confirm Revert';
    modalRef.componentInstance.content = `You are about to <strong>revert this action</strong> — ${count} SKU(s) that were ${action} will be restored to their previous state.`;
    modalRef.componentInstance.confirmText = 'Revert';

    modalRef.result
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.scrollToTop();
        this.isReverting = true;
        this.isDetailLoading = true;
        this.adHistoryLogService.reverts(idsToRevert).subscribe(
          () => {
            this.loadLogs(true);
            if (this.isDetailOpen && this.selectedLog) {
              this.loadDetail(this.selectedLog._id);
            } else if (this.isDetailOpen && this.isCombineLog) {
              this.loadCombinedDetail();
            } else {
              this.isDetailLoading = false;
            }
            this.isReverting = false;
            this.productAdsActionService.notifyExternalMutation();
          },
          (err) => {
            console.error('Revert failed', err);
            this.isLoading = false;
            this.isReverting = false;
            this.isDetailLoading = false;
          },
        );
      })
      .catch(() => {
        /* dismissed */
      });
  }

  public toggleDrawer(event: Event): void {
    event.stopPropagation();
    if (this.isOpened) {
      this.closeDrawer();
    } else {
      // Reset any external filter override so the normal button fetches ALL logs
      this.filterOverride = null;
      this.selectedActionTypes = [];
      this.prevSelectedActionTypes = [];
      // this.selectedActionType = this.actionTypes[0]; // 'All actions'
      this.range = {
        type: DateRangePeriod.LAST_90_DAYS,
        start: subDays(startOfToday(), 90),
        end: startOfToday(),
      };
      this.openDrawer();
    }
  }

  public openDrawer(): void {
    this.isOpened = true;
    document.body.style.overflow = 'hidden';
    this.currentPage = 0;
    this.loadLogs();
  }

  public closeDrawer(): void {
    this.filterOverride = null;
    this.isOpened = false;
    document.body.style.overflow = '';
  }

  public trackByLogId(index: number, item: AdHistoryLog): string {
    return item._id;
  }

  public scrollToTop(): void {
    if (this.drawerBody && this.drawerBody.nativeElement) {
      this.drawerBody.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private buildDetailRootItems(detail: AdHistoryLogDetail): any[] {
    return detail.details.data.map((item: any) => {
      const snap = item.productAdsSnapshot ?? {};
      return {
        _id: item._id,
        itemKey: item.itemKey,
        sku: item.sku || '',
        asin: item.asin || '',
        prevState: item.prevState || '',
        newState: item.newState || '',
        inventoryStatus: snap.inventory_status || snap.inventoryStatus || '',
        suggestAction: snap.suggest_action || snap.suggestAction || '',
        inventoryAvailable:
          snap.inventory_available != null
            ? Number(snap.inventory_available)
            : snap.inventoryAvailable != null
              ? Number(snap.inventoryAvailable)
              : '',
        saleVelocity:
          snap.sale_velocity != null
            ? Number(snap.sale_velocity)
            : snap.saleVelocity != null
              ? Number(snap.saleVelocity)
              : '',
        daysUntilStockout:
          snap.days_until_stockout != null
            ? Number(snap.days_until_stockout)
            : snap.daysUntilStockout != null
              ? Number(snap.daysUntilStockout)
              : '',
        min:
          snap.min_days_stockout != null
            ? Number(snap.min_days_stockout)
            : snap.minDaysStockout != null
              ? Number(snap.minDaysStockout)
              : '',
        max:
          snap.max_days_stockout != null
            ? Number(snap.max_days_stockout)
            : snap.maxDaysStockout != null
              ? Number(snap.maxDaysStockout)
              : '',
        reason: item.reason || '',
        ...(this.isCombineLog && { updatedAt: snap.updated_at }),
        ...(this.isCombineLog && {
          logId: '#' + item._id.toString().slice(-8),
        }),
        errors: (item.error || []).join('; '),
        campaignState: '',
        adGroup: '',
        adId: '',
        _campaigns: item.campaigns || [],
      };
    });
  }

  // this function is used by the parent component to pass the filter data to this component
  // example: breadcrumb component calls this function when the user clicks on a log type
  public onFilterChange(filter: { triggerType: string; ids?: string[] }): void {
    this.filterOverride = {
      triggerType: filter.triggerType,
      ids: filter.ids,
    };
    this.range = {
      type: DateRangePeriod.YESTERDAY,
      start: subDays(startOfToday(), 1),
      end: startOfToday(),
    };
    if (filter.ids) {
      this.selectedActionTypes = this.actionTypes.filter(
        (type) => type.value === 'AUTO_PAUSE' || type.value === 'AUTO_ENABLE',
      );
    }
    // 2. Open the side panel (skip the document:click handler so it doesn't close immediately)
    this.skipNextDocumentClick = true;
    this.openDrawer();
  }

  public loadCombinedDetail(): void {
    this.isDetailLoading = true;

    // Build query params (pagination, sort, search)
    const sortEntry =
      this.detailSort && this.detailSort.length > 0 ? this.detailSort[0] : null;
    const params: any = {
      limit: this.detailPageSize,
      offset: this.detailSkip,
      search: this.detailSearch,
    };

    if (sortEntry) {
      params.sortField = sortEntry.field;
      params.sortDir = sortEntry.dir;
    }
    // Add the IDs and Action from the alert
    const combinedParams = {
      ...params,
      ids: this.filterOverride.ids || [],
      action: this.filterOverride.action,
    };
    this.adHistoryLogService.getCombinedDetails(combinedParams).subscribe(
      (res) => {
        this.selectedLogDetail = res;
        this.combinedStats = res.headers.reduce(
          (cur, prev) => {
            cur.totalCount += prev.totalCount;
            cur.appliedCount += prev.appliedCount;
            cur.failedCount += prev.failedCount;
            return cur;
          },
          { totalCount: 0, appliedCount: 0, failedCount: 0 },
        );
        this.detailTotal = res.details.total;
        this.detailRootItems = this.buildDetailRootItems(res);
        this.isDetailLoading = false;
      },
      () => {
        this.isDetailLoading = false;
      },
    );
  }

  public onCombinedFilterChange(filter: {
    action: string;
    ids: string[];
  }): void {
    // 1. Store filters in the override
    this.filterOverride = {
      action: filter.action,
      ids: filter.ids,
    };
    // 2. Set UI flags to open the Detail Modal directly
    this.isDetailOpen = true;
    this.isCombineLog = true;
    this.selectedLog = null; // We are in combined mode, so no single log selected
    this.detailSkip = 0;

    // 3. Load the combined data
    this.loadCombinedDetail();
  }

  public isRevertExists(log_id: string) {
    const existOne = this.logs.find((log) => log.sourceLogId != undefined && log.sourceLogId === log_id);
    return existOne && existOne.triggerType === 'REVERT';
  }

  public isAnyReverted(headers: AdHistoryLog[]): boolean {
    if (!headers || !Array.isArray(headers)) {
      return false;
    }
    return headers.some((header) => this.isRevertExists(header._id));
  }
}
